/**
 * The creator's design values, in the headless package so every surface reads the same ones.
 *
 * Plain data, no CSS and no `StyleSheet`: the web package turns these into custom properties
 * (`eventCreatorTokensCss`) and React Native uses them directly. Without this the three surfaces
 * drift — a slightly different blue and a slightly different grey per platform — which is exactly
 * the "two designs sharing a product" problem the component exists to avoid.
 *
 * A host that wants its own look overrides the CSS variables on the web, or passes its own values
 * on native; these are the defaults, not a lock-in.
 */

export interface EventCreatorPalette {
  /** Body text. */
  readonly fg: string;
  /** Labels, hints, secondary text. */
  readonly muted: string;
  /** The component's own surface. */
  readonly bg: string;
  /** The page behind it — used by a host's chrome, not by the component. */
  readonly page: string;
  /** Panels inside the component (preview, links). */
  readonly surface: string;
  readonly border: string;
  readonly accent: string;
  /** Accent at low emphasis — a selected chip's fill. */
  readonly accentSoft: string;
  /** Text on top of `accent`. */
  readonly accentFg: string;
  readonly danger: string;
  readonly codeBg: string;
  readonly codeFg: string;
}

export const LIGHT_PALETTE: EventCreatorPalette = {
  fg: '#16181d',
  muted: '#626a7a',
  bg: '#ffffff',
  page: '#f2f4f8',
  surface: '#f6f7f9',
  border: '#d9dde5',
  accent: '#2f5bea',
  accentSoft: '#eef2fe',
  accentFg: '#ffffff',
  danger: '#b42318',
  codeBg: '#16181d',
  codeFg: '#e9edf5',
};

export const DARK_PALETTE: EventCreatorPalette = {
  fg: '#e8eaf0',
  muted: '#9aa3b5',
  bg: '#14161b',
  page: '#0e1015',
  surface: '#1c1f27',
  border: '#333846',
  accent: '#7c9bff',
  accentSoft: '#22283a',
  accentFg: '#10131a',
  danger: '#ff8b80',
  codeBg: '#0b0d12',
  codeFg: '#e9edf5',
};

export const RADIUS = {
  /** Inputs, buttons, panels. */
  field: 10,
  /** The card a host wraps the component in. */
  card: 14,
  pill: 999,
} as const;

/** The smallest comfortable touch target in both iOS and Android guidance. */
export const TOUCH_TARGET = 44;

export const TYPE = {
  /** Field labels and chips. */
  small: 13,
  body: 15,
  /** Inputs stay at 16 — iOS Safari zooms the page when a focused field is smaller. */
  input: 16,
} as const;

export function eventCreatorPalette(scheme: 'light' | 'dark'): EventCreatorPalette {
  return scheme === 'dark' ? DARK_PALETTE : LIGHT_PALETTE;
}
